import { Capacitor } from '@capacitor/core';

let LocalNotifications: any = null;
let CapacitorAlarm: any = null;
let DivineAlarm: any = null;

if (Capacitor.isNativePlatform()) {
  try {
    LocalNotifications = require('@capacitor/local-notifications').LocalNotifications;
  } catch {}
  try {
    CapacitorAlarm = require('@capgo/capacitor-alarm').CapacitorAlarm;
  } catch {}
  try {
    DivineAlarm = require('../plugins/DivineAlarm').default;
  } catch {}
}

// Single channel ID — delete + recreate ensures fresh settings
const CHANNEL_ID = 'divine_reminder';

// Setup ONE channel with the selected mantra's sound
// Delete + recreate to bypass Android's channel caching
export async function setupDivineChannel(mantraSoundId: string): Promise<void> {
  if (!Capacitor.isNativePlatform() || !LocalNotifications) return;

  try {
    // Delete old channel to reset cached settings
    try { await LocalNotifications.deleteChannel({ id: CHANNEL_ID }); } catch {}

    await LocalNotifications.createChannel({
      id: CHANNEL_ID,
      name: 'दिव्य स्मरण',
      description: 'Mantra reminder with audio',
      importance: 4, // HIGH (not MAX — some devices block MAX)
      sound: `${mantraSoundId}.mp3`,
      vibration: true,
      visibility: 1,
    });
    console.log(`[Channel] Created: ${CHANNEL_ID} sound=${mantraSoundId}.mp3`);
  } catch (err) {
    console.error('[Channel] Failed:', err);
  }
}

// Backward compat — called from App.tsx on startup
export async function initAllNotificationChannels(): Promise<void> {
  if (!Capacitor.isNativePlatform() || !LocalNotifications) return;
  // Read saved config to know which mantra sound to use
  try {
    const saved = localStorage.getItem('ram_naam_reminder_config');
    const config = saved ? JSON.parse(saved) : null;
    if (config?.enabled && config?.mantraSoundName) {
      await setupDivineChannel(config.mantraSoundName);
    } else {
      // Default channel with default sound
      await setupDivineChannel('mantra_ram');
    }
  } catch {
    await setupDivineChannel('mantra_ram');
  }
}

// Cancel Capacitor LocalNotifications only (NOT native DivineAlarm — that stays persistent)
export async function cancelAllDivineNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  // DO NOT cancel DivineAlarm native plugin here — it must persist through app kills
  // Native alarms are only cancelled via nativeAlarmService.cancelNativeDivineReminder()

  // Cancel LocalNotifications
  if (LocalNotifications) {
    try {
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel({
          notifications: pending.notifications.map((n: any) => ({ id: n.id }))
        });
      }
      const toCancel = [];
      for (let i = 0; i < 100; i++) toCancel.push({ id: 7000 + i });
      await LocalNotifications.cancel({ notifications: toCancel });
      await LocalNotifications.removeAllDeliveredNotifications();
    } catch {}
  }
}

// Schedule notifications using multiple layers for maximum reliability:
// Layer 1: Custom DivineAlarm plugin (native AlarmManager — MOST reliable)
// Layer 2: LocalNotifications (Capacitor built-in — secondary)
// Layer 3: FCM server push (handled by backend — guaranteed with internet)
export async function scheduleAllNotifications(
  mantraSoundName: string,
  mantraText: string,
  intervalMs: number,
  startFrom: number
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  // Ensure channel exists with correct sound (for Local + FCM notifications)
  await setupDivineChannel(mantraSoundName);

  const intervalMinutes = Math.round(intervalMs / 60000);

  // Layer 1: Native AlarmManager handled by nativeAlarmService.ts (called from DivineReminder.tsx)
  // DO NOT call DivineAlarm here — it causes duplicate cancelType() which creates race conditions

  // Layer 2: Capacitor LocalNotifications (secondary — works in foreground/background)
  if (LocalNotifications) {
    try {
      const maxNotifs = Math.min(50, Math.floor((24 * 60 * 60 * 1000) / intervalMs));
      const notifications: any[] = [];
      for (let i = 0; i < maxNotifs; i++) {
        const fireTime = startFrom + intervalMs * (i + 1);
        notifications.push({
          id: 7000 + i,
          title: 'दिव्य स्मरण',
          body: mantraText,
          schedule: {
            at: new Date(fireTime),
            allowWhileIdle: true,
          },
          channelId: CHANNEL_ID,
        });
      }
      if (notifications.length > 0) {
        await LocalNotifications.schedule({ notifications });
        console.log(`[LocalNotif] ${notifications.length} scheduled`);
      }
    } catch (err) {
      console.error('[LocalNotif] Schedule failed:', err);
    }
  }
}

// Safety net: if disabled, cancel everything
export async function setupDisableGuard(): Promise<void> {
  if (!Capacitor.isNativePlatform() || !LocalNotifications) return;
  try {
    await LocalNotifications.addListener('localNotificationReceived', async () => {
      const saved = localStorage.getItem('ram_naam_reminder_config');
      const config = saved ? JSON.parse(saved) : { enabled: false };
      if (!config.enabled) {
        console.warn('[Guard] Disabled. Cancelling all.');
        await cancelAllDivineNotifications();
      }
    });
  } catch {}
}
